import { groupInput, input, nonEmptyValidator } from "@redotech/ui/form";
import { RefObject, useState } from "react";
import { useWindowEventListener } from "./event";
import { useInput } from "./form";

const barcodeScanForm = groupInput({
  barcode: input<string>({ validator: nonEmptyValidator }),
});

/**
 * Character that triggers "ready" mode for barcode scanners.
 * Many barcode scanners can be configured to prefix scans with this character.
 */
export const SCAN_CHAR = "&";

interface UseBarcodeScannerOptions {
  /**
   * Callback fired when a barcode is scanned (Enter pressed with value).
   */
  onScan: (value: string) => void;
  /**
   * Optional ref to the input element. When the input is focused,
   * keystrokes are not captured by the scanner listener.
   */
  inputRef?: RefObject<HTMLInputElement>;
  /**
   * Initial value for the barcode input.
   * @default ""
   */
  initialValue?: string;
}

interface UseBarcodeScannerResult {
  /**
   * Current barcode value.
   */
  value: string;
  /**
   * Set the barcode value.
   */
  setValue: (value: string) => void;
  /**
   * Clear the barcode value.
   */
  clear: () => void;
}

/**
 * Hook for handling barcode scanner input.
 *
 * Captures keyboard input globally and builds up a barcode value.
 * When Enter is pressed, the onScan callback is fired with the value.
 *
 * Supports barcode scanners that prefix scans with the SCAN_CHAR ("&").
 * When SCAN_CHAR is detected, the scanner enters "ready" mode and clears
 * on the next non-special character.
 */
export function useBarcodeScanner({
  onScan,
  inputRef,
  initialValue = "",
}: UseBarcodeScannerOptions): UseBarcodeScannerResult {
  const inputState = useInput(barcodeScanForm, { barcode: initialValue });
  const { barcode } = inputState.inputs;
  const [readyCommand, setReadyCommand] = useState(false);

  useWindowEventListener(window, "keydown", (e) => {
    // Skip if user is typing in the input field - let form submission work normally
    if (inputRef?.current && document.activeElement === inputRef.current) {
      return;
    }

    if (e.key === "Enter" && barcode.value.trim()) {
      e.preventDefault();
      const barcodeValue = barcode.value.trim();
      onScan(barcodeValue);
      return;
    }

    if (e.key === SCAN_CHAR) {
      setReadyCommand(true);
    } else if (e.key === "Shift") {
      // Ignore shift key
    } else if (readyCommand) {
      setReadyCommand(false);
      barcode.setValue("");
    } else if (!e.ctrlKey && !e.metaKey && e.key.length === 1) {
      e.preventDefault();
      const currentValue = barcode.value + e.key;
      barcode.setValue(currentValue);
    }
  });

  return {
    value: barcode.value,
    setValue: barcode.setValue,
    clear: () => barcode.setValue(""),
  };
}
